import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, BookOpen, Twitter, Edit3, Trash2, Hash } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import SiteDisclaimer from '@/components/SiteDisclaimer';
import LoginModal from '@/components/LoginModal';
import JournalEntryModal from '@/components/JournalEntryModal';
import InlineStockModal from '@/components/InlineStockModal';
import { shareEntryToX } from '@/utils/journalShare';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Single-tag view of the journal — `/journal/tag/:tag`. Useful pre-
// tax-season ("show me everything I tagged #yield-trap") or as a
// landing page from a tag chip click. Login-gated like the main
// journal page; data is private to each user.

const TYPE_LABEL = {
  thesis_add: 'Thesis on Add',
  thesis_sell: 'Thesis on Sell',
  dividend_cut: 'Dividend Cut',
  ex_div: 'Ex-Div Week',
  free: 'Note',
  review: 'Review',
  jnl_starter: 'JNL Starter',
};

export default function JournalTag() {
  const { tag } = useParams();
  const { user } = useAuth();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showLogin, setShowLogin] = useState(false);
  const [editing, setEditing] = useState(null);
  const [openStockTicker, setOpenStockTicker] = useState(null);

  useEffect(() => {
    if (!user) { setLoading(false); return; }
    let cancelled = false;
    setLoading(true);
    axios.get(`${API}/journal/entries`, { params: { tag, limit: 500 }, withCredentials: true })
      .then((res) => { if (!cancelled) setEntries(res.data?.entries || []); })
      .catch(() => { if (!cancelled) setEntries([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [tag, user]);

  const handleDelete = async (entryId) => {
    if (!window.confirm('Delete this journal entry permanently?')) return;
    try {
      await axios.delete(`${API}/journal/entries/${entryId}`, { withCredentials: true });
      setEntries((prev) => prev.filter((e) => e.entry_id !== entryId));
      toast.success('Entry deleted');
    } catch (err) {
      toast.error('Delete failed', { description: err?.response?.data?.detail || err.message });
    }
  };

  if (user === null || user === false) {
    return (
      <div className="min-h-screen min-h-[100svh] bg-background">
        <div className="max-w-2xl mx-auto px-4 py-8">
          <div className="rounded-2xl border-2 border-violet-300 dark:border-violet-700 bg-muted/20 p-6 text-center" data-testid="journal-tag-login-gate">
            <Hash className="w-6 h-6 mx-auto text-violet-600 dark:text-violet-400 mb-2" />
            <h1 className="text-xl font-bold">#{tag}</h1>
            <p className="text-sm text-muted-foreground mt-2">Log in to view your journal entries on this tag.</p>
            <Button
              onClick={() => setShowLogin(true)}
              className="mt-4 bg-violet-600 hover:bg-violet-700 text-white"
              data-testid="journal-tag-login-cta"
            >
              Sign Up / Log In
            </Button>
          </div>
        </div>
        {showLogin && <LoginModal open={showLogin} onClose={() => setShowLogin(false)} />}
      </div>
    );
  }

  return (
    <div className="min-h-screen min-h-[100svh] bg-background">
      <div className="max-w-3xl mx-auto px-3 sm:px-4 py-5 space-y-4">
        <Link to="/journal" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground" data-testid="journal-tag-back-btn">
          <ArrowLeft className="w-4 h-4" /> All entries
        </Link>

        <div className="flex items-center gap-2">
          <Hash className="w-6 h-6 text-violet-600 dark:text-violet-400" />
          <h1 className="text-2xl font-bold">#{tag}</h1>
          <span className="text-sm text-muted-foreground">({entries.length})</span>
        </div>

        {loading && <div className="text-center py-10 text-muted-foreground">Loading…</div>}

        {!loading && entries.length === 0 && (
          <div className="text-center py-12 border-2 border-dashed border-border rounded-xl" data-testid="journal-tag-empty">
            <BookOpen className="w-10 h-10 mx-auto text-muted-foreground mb-2" />
            <p className="text-foreground font-semibold">No entries on #{tag} yet.</p>
            <p className="text-sm text-muted-foreground mt-1">Tag any future entry with this label to see it here.</p>
          </div>
        )}

        <div className="space-y-3">
          {entries.map((e) => (
            <div
              key={e.entry_id}
              className="rounded-xl border-2 border-border bg-muted/20 p-4 hover:border-violet-400/60 transition-colors"
              data-testid={`journal-tag-entry-${e.entry_id}`}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="text-[10px] uppercase font-bold text-muted-foreground mb-1">
                    {TYPE_LABEL[e.entry_type] || 'Note'}
                    {' · '}
                    {new Date(e.created_at).toLocaleDateString()}
                  </div>
                  {e.ticker && (
                    <button
                      type="button"
                      onClick={() => setOpenStockTicker(e.ticker)}
                      className="inline-block px-2 py-0.5 rounded-md text-[11px] font-mono font-bold bg-background border hover:bg-accent hover:border-violet-400 mb-1"
                      data-testid={`journal-tag-entry-ticker-${e.entry_id}`}
                    >
                      {e.ticker}
                    </button>
                  )}
                  {e.title && <h3 className="text-base font-bold">{e.title}</h3>}
                  <p className="text-sm text-foreground/90 whitespace-pre-wrap mt-1">{e.body}</p>
                  {e.tags && e.tags.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-2">
                      {e.tags.map((t) => (
                        <Link
                          key={t}
                          to={`/journal/tag/${t}`}
                          className={`text-[10px] hover:underline ${t === tag ? 'text-violet-700 dark:text-violet-300 font-semibold' : 'text-muted-foreground'}`}
                          data-testid={`journal-tag-link-${t}`}
                        >
                          #{t}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
                <div className="flex flex-col gap-1 shrink-0">
                  <button
                    onClick={() => shareEntryToX(e)}
                    className="p-1.5 rounded hover:bg-sky-100 dark:hover:bg-sky-950/40 text-sky-600"
                    title="Share to X"
                    data-testid={`journal-tag-share-x-${e.entry_id}`}
                  >
                    <Twitter className="w-3.5 h-3.5" />
                  </button>
                  <button
                    onClick={() => setEditing(e)}
                    className="p-1.5 rounded hover:bg-muted text-muted-foreground hover:text-foreground"
                    title="Edit"
                    data-testid={`journal-tag-edit-${e.entry_id}`}
                  >
                    <Edit3 className="w-3.5 h-3.5" />
                  </button>
                  <button
                    onClick={() => handleDelete(e.entry_id)}
                    className="p-1.5 rounded hover:bg-rose-100 dark:hover:bg-rose-950/40 text-rose-600"
                    title="Delete"
                    data-testid={`journal-tag-delete-${e.entry_id}`}
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        <SiteDisclaimer />
      </div>

      {editing && (
        <JournalEntryModal
          entry={editing}
          onClose={() => setEditing(null)}
          onSaved={() => setEditing(null)}
        />
      )}

      {/* Round 71p: in-place Stock Modal — keeps user on this tag
          page when closed instead of bouncing them to Dashboard. */}
      <InlineStockModal
        ticker={openStockTicker}
        onClose={() => setOpenStockTicker(null)}
      />
    </div>
  );
}
